import type { EditorState, TransactionSpec } from "@codemirror/state";
import type { EditorView, KeyBinding } from "@codemirror/view";
import { Effect, Queue, Schema as S, Stream } from "effect";
import * as Command from "foldkit/command";
import type { Html, HtmlBuilder } from "foldkit/html";
import { defineMessageUnion } from "foldkit/message";
import * as Mount from "foldkit/mount";

import { i18next } from "../i18n";
import * as Icons from "./icons";

const ICON = {
	bold: Icons.Bold,
	italic: Icons.Italic,
	strikethrough: Icons.Strikethrough,
	"heading-1": Icons.Heading1,
	"heading-2": Icons.Heading2,
	"heading-3": Icons.Heading3,
	list: Icons.List,
	"list-ordered": Icons.ListOrdered,
	quote: Icons.Quote,
	link: Icons.Link,
	code: Icons.Code,
	"file-code": Icons.FileCode,
	"square-check-big": Icons.CheckSquare,
} as const;

/**
 * Markdown source editor (KTD6): CodeMirror 6 on a `Mount.defineStream`.
 * Behavior ported from v1 `MarkdownEditor.tsx` + `markdownToolbarActions.ts`:
 * markdown language, line wrapping, placeholder, undo/redo, no gutters,
 * Mod-b / Mod-i / Mod-k shortcuts, and a theme keyed to the DaisyUI theme.
 *
 * Mount args are frozen at mount time, so the editor is seeded with
 * `initialText` once. Everything afterwards flows one way: keystrokes emit
 * `EditorChanged`, and the Model reaches the editor only through the
 * Commands below (`SetEditorText`, `EditorAction`), which look the view up
 * in the registry by `id`. Use a stable id (the entry id, or `"new"` for the
 * add-entry form) so re-renders never remount.
 */

const views = new Map<string, EditorView>();

/** Registry by id; exported so the Commands and the tests share one. */
export const registry = {
	get: (id: string): EditorView | undefined => views.get(id),
	set: (id: string, view: EditorView): void => {
		views.set(id, view);
	},
	delete: (id: string): void => {
		views.delete(id);
	},
};

export const Message = defineMessageUnion({
	EditorChanged: { id: S.String, text: S.String },
	EditorFocusChanged: { id: S.String, focused: S.Boolean },
});
export type Message = typeof Message.Type;

export const CommandMessage = defineMessageUnion({ CompletedEditorAction: {} });
export type CommandMessage = typeof CommandMessage.Type;

export const Action = S.Literals([
	"bold",
	"italic",
	"strikethrough",
	"inlineCode",
	"heading1",
	"heading2",
	"heading3",
	"bulletList",
	"numberedList",
	"blockquote",
	"taskCheckbox",
	"link",
	"codeBlock",
]);
export type Action = typeof Action.Type;

/** Toolbar order and i18n keys, as v1 `MarkdownToolbar.tsx`. */
export const TOOLBAR: ReadonlyArray<
	Readonly<{
		action: Action;
		i18nKey: string;
		shortcut?: string;
		icon: keyof typeof ICON;
	}>
> = [
	{ action: "bold", i18nKey: "editor.bold", shortcut: "Ctrl+B", icon: "bold" },
	{
		action: "italic",
		i18nKey: "editor.italic",
		shortcut: "Ctrl+I",
		icon: "italic",
	},
	{
		action: "strikethrough",
		i18nKey: "editor.strikethrough",
		icon: "strikethrough",
	},
	{ action: "heading1", i18nKey: "editor.heading1", icon: "heading-1" },
	{ action: "heading2", i18nKey: "editor.heading2", icon: "heading-2" },
	{ action: "heading3", i18nKey: "editor.heading3", icon: "heading-3" },
	{ action: "bulletList", i18nKey: "editor.bulletList", icon: "list" },
	{
		action: "numberedList",
		i18nKey: "editor.numberedList",
		icon: "list-ordered",
	},
	{ action: "blockquote", i18nKey: "editor.blockquote", icon: "quote" },
	{ action: "link", i18nKey: "editor.link", shortcut: "Ctrl+K", icon: "link" },
	{ action: "inlineCode", i18nKey: "editor.inlineCode", icon: "code" },
	{ action: "codeBlock", i18nKey: "editor.codeBlock", icon: "file-code" },
	{
		action: "taskCheckbox",
		i18nKey: "editor.taskCheckbox",
		icon: "square-check-big",
	},
];

/** v1 `MarkdownToolbar`: one button per action; the page turns the click into `EditorAction`. */
export const toolbar = <Message>(
	h: HtmlBuilder<Message>,
	toMessage: (action: Action) => Message,
): Html =>
	h.div(
		[
			h.Role("toolbar"),
			h.AriaLabel(i18next.t("editor.toolbar")),
			h.Class(
				"flex flex-wrap gap-1 px-2 py-1 border-b border-base-300 bg-base-200",
			),
		],
		TOOLBAR.map((button) => {
			const label = i18next.t(button.i18nKey);
			return h.button(
				[
					h.Type("button"),
					h.Class("btn btn-ghost btn-sm btn-square"),
					h.AriaLabel(label),
					h.Title(button.shortcut ? `${label} (${button.shortcut})` : label),
					h.OnClick(toMessage(button.action)),
				],
				[ICON[button.icon](h, { size: 16 })],
			);
		}),
	);

// TRANSACTIONS (pure over EditorState; v1 markdownToolbarActions.ts)

const wrap = (
	state: EditorState,
	prefix: string,
	suffix: string,
): TransactionSpec => {
	const { from, to } = state.selection.main;
	const selected = state.sliceDoc(from, to);
	return selected.length > 0
		? {
				changes: { from, to, insert: `${prefix}${selected}${suffix}` },
				selection: {
					anchor: from + prefix.length,
					head: from + prefix.length + selected.length,
				},
			}
		: {
				changes: { from, to, insert: `${prefix}${suffix}` },
				selection: { anchor: from + prefix.length },
			};
};

const prependLines = (state: EditorState, prefix: string): TransactionSpec => {
	const { from, to } = state.selection.main;
	const start = state.doc.lineAt(from).number;
	const end = state.doc.lineAt(to).number;
	const changes = [];
	for (let n = start; n <= end; n++) {
		changes.push({ from: state.doc.line(n).from, insert: prefix });
	}
	return { changes };
};

const link = (state: EditorState): TransactionSpec => {
	const { from, to } = state.selection.main;
	const selected = state.sliceDoc(from, to);
	if (selected.length > 0) {
		// v1 used `+ 2` and selected "(ur"; the intent (select "url") is kept here.
		const urlStart = selected.length + 3;
		return {
			changes: { from, to, insert: `[${selected}](url)` },
			selection: { anchor: from + urlStart, head: from + urlStart + 3 },
		};
	}
	return {
		changes: { from, insert: "[text](url)" },
		selection: { anchor: from + 7, head: from + 10 },
	};
};

const codeBlock = (state: EditorState): TransactionSpec => {
	const { from } = state.selection.main;
	return {
		changes: { from, insert: "```\n\n```" },
		selection: { anchor: from + 4 },
	};
};

/** The transaction a toolbar action applies to the current state. */
export const transaction = (
	action: Action,
	state: EditorState,
): TransactionSpec => {
	switch (action) {
		case "bold":
			return wrap(state, "**", "**");
		case "italic":
			return wrap(state, "*", "*");
		case "strikethrough":
			return wrap(state, "~~", "~~");
		case "inlineCode":
			return wrap(state, "`", "`");
		case "heading1":
			return prependLines(state, "# ");
		case "heading2":
			return prependLines(state, "## ");
		case "heading3":
			return prependLines(state, "### ");
		case "bulletList":
			return prependLines(state, "- ");
		case "numberedList":
			return prependLines(state, "1. ");
		case "blockquote":
			return prependLines(state, "> ");
		case "taskCheckbox":
			return prependLines(state, "- [ ] ");
		case "link":
			return link(state);
		case "codeBlock":
			return codeBlock(state);
	}
};

const apply = (view: EditorView, action: Action): void => {
	view.dispatch(transaction(action, view.state));
	view.focus();
};

// COMMANDS

/** Applies a toolbar action (or shortcut) to the registry-held view. */
export const EditorAction = Command.define("EditorAction", {
	args: { id: S.String, action: Action },
	messages: [CommandMessage.CompletedEditorAction],
	execute: ({ id, action }) =>
		Effect.sync(() => {
			const view = views.get(id);
			if (view) apply(view, action);
			return CommandMessage.CompletedEditorAction();
		}),
});

/** Model→editor sync (e.g. the add-entry reset): replaces the whole document. */
export const SetEditorText = Command.define("SetEditorText", {
	args: { id: S.String, text: S.String },
	messages: [CommandMessage.CompletedEditorAction],
	execute: ({ id, text }) =>
		Effect.sync(() => {
			const view = views.get(id);
			if (view && view.state.doc.toString() !== text) {
				view.dispatch({
					changes: { from: 0, to: view.state.doc.length, insert: text },
				});
			}
			return CommandMessage.CompletedEditorAction();
		}),
});

/** Focuses the registry-held view (KTD4): the host div is not focusable, so `Dom.focus` cannot reach CodeMirror. */
export const FocusEditor = Command.define("FocusEditor", {
	args: { id: S.String },
	messages: [CommandMessage.CompletedEditorAction],
	execute: ({ id }) =>
		Effect.sync(() => {
			views.get(id)?.focus();
			return CommandMessage.CompletedEditorAction();
		}),
});

// MOUNT

/** Opt-in composers submit through their normal form validation and save path. */
export const submitEntryShortcut: KeyBinding = {
	key: "Shift-Enter",
	stopPropagation: true,
	run: (view) => {
		const form = view.dom.closest<HTMLFormElement>(
			"form[data-submit-entry-shortcut]",
		);
		if (!form) return false;
		form.requestSubmit();
		return true;
	},
};

const cssVar = (name: string): string => {
	const value = getComputedStyle(document.documentElement)
		.getPropertyValue(name)
		.trim();
	return value ? `oklch(${value})` : "";
};

export const MarkdownEditor = Mount.defineStream("MarkdownEditor", {
	args: {
		id: S.String,
		initialText: S.String,
		placeholder: S.String,
		dark: S.Boolean,
		minHeight: S.Number,
	},
	messages: [Message.EditorChanged, Message.EditorFocusChanged],
	execute: ({ element, id, initialText, placeholder, dark, minHeight }) =>
		Stream.callback<Message>((queue) =>
			Effect.gen(function* () {
				yield* Effect.acquireRelease(
					Effect.promise(async () => {
						const [
							{ EditorState },
							{ EditorView, keymap, placeholder: placeholderExt },
							{ markdown },
							{ defaultKeymap, history, historyKeymap },
						] = await Promise.all([
							import("@codemirror/state"),
							import("@codemirror/view"),
							import("@codemirror/lang-markdown"),
							import("@codemirror/commands"),
						]);
						const emit = (message: Message) => {
							Queue.offerUnsafe(queue, message);
						};
						const shortcut = (key: string, action: Action) => ({
							key,
							run: (view: EditorView) => {
								apply(view, action);
								return true;
							},
						});
						const fg = cssVar("--bc");
						const view = new EditorView({
							parent: element,
							state: EditorState.create({
								doc: initialText,
								extensions: [
									markdown(),
									history(),
									EditorView.lineWrapping,
									placeholderExt(placeholder),
									keymap.of([
										submitEntryShortcut,
										shortcut("Mod-b", "bold"),
										shortcut("Mod-i", "italic"),
										shortcut("Mod-k", "link"),
										...historyKeymap,
										...defaultKeymap,
									]),
									EditorView.theme(
										{
											"&": {
												backgroundColor: cssVar("--b1"),
												color: fg,
												minHeight: `${minHeight}px`,
											},
											// Fill the box so a click anywhere in it lands in the document, not just on the first line.
											".cm-content": {
												caretColor: fg,
												minHeight: `${minHeight}px`,
											},
											".cm-cursor, .cm-dropCursor": { borderLeftColor: fg },
											".cm-placeholder": { color: fg.replace(")", " / 0.4)") },
										},
										{ dark },
									),
									EditorView.updateListener.of((update) => {
										if (update.docChanged) {
											emit(
												Message.EditorChanged({
													id,
													text: update.state.doc.toString(),
												}),
											);
										}
										if (update.focusChanged) {
											emit(
												Message.EditorFocusChanged({
													id,
													focused: update.view.hasFocus,
												}),
											);
										}
									}),
								],
							}),
						});
						views.set(id, view);
						return view;
					}),
					(view) =>
						Effect.sync(() => {
							// Release is async; a remount under the same id may already own the slot.
							if (views.get(id) === view) views.delete(id);
							view.destroy();
						}),
				);
				return yield* Effect.never;
			}),
		),
});
